import React from 'react';
import clsx from 'clsx';
import styles from './styles.module.css';
import Header from '@pixum/header';
import Text, { TEXT_VARIANT, TextTag } from '@pixum/text';
import type { EditorialSectionNode, ImageNode } from 'data-layer';
import { Image, IMAGE_TRIM } from '../image';
import { RichTextBody, isRichTextEmpty } from '../richtText';
import { ImageLoading } from '@pixum/logo';
import type { DeviceOrientedComponentProps } from '../../types';

interface EditorialSectionCollectionProps extends DeviceOrientedComponentProps {
  sections: EditorialSectionNode[];
  sectionHeadingLevel?: 2 | 3;
}

function getAspectRatioStyle(image: ImageNode, isSingleImage: boolean): React.CSSProperties | undefined {
  if (image.isPortrait) return undefined;

  if (!isSingleImage) return { aspectRatio: '1 / 1' };

  if (image.imageTagWidth && image.imageTagHeight)
    return { aspectRatio: `${image.imageTagWidth} / ${image.imageTagHeight}` };
}

export function EditorialSectionCollection({
  sections,
  device,
  sectionHeadingLevel = 3
}: EditorialSectionCollectionProps) {
  if (!sections.length) {
    return null;
  }

  return (
    <>
      {sections.map(section => (
        <article key={section.id} className={styles.editorialSection}>
          <div className={styles.textHeader}>
            {section.title && (
              <Header
                headlineText={section.title}
                headlineVariant={sectionHeadingLevel === 2 ? TEXT_VARIANT.TITLE_2 : TEXT_VARIANT.TITLE_3}
                level={sectionHeadingLevel}
                textAlignment="left"
              />
            )}
            {!isRichTextEmpty(section.richText) && <RichTextBody text={section.richText} />}
          </div>

          {section.images.length > 0 && (
            <div className={styles.images}>
              {section.images.map(item => (
                <figure key={item.image.url} className={styles.imageItem}>
                  <div
                    className={clsx(styles.imageWrapper, item.image.isPortrait && styles.imageWrapperPortrait)}
                    style={getAspectRatioStyle(item.image, section.images.length === 1)}
                  >
                    <Image image={item.image} loading={ImageLoading.Lazy} device={device} trim={IMAGE_TRIM.NONE} />
                  </div>
                  {item.caption && (
                    <figcaption>
                      <Text tag={TextTag.SPAN} variant={TEXT_VARIANT.CAPTION}>
                        {item.caption}
                      </Text>
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}

          {!isRichTextEmpty(section.textAfterMedia) && <RichTextBody text={section.textAfterMedia} />}
        </article>
      ))}
    </>
  );
}
